/**
 * 触摸端：**长按当右键**。
 *
 * ## 抽出来的理由
 *
 * `Playground.vue` 有 5000 多行，改一处要找半天。按「一块逻辑一个文件」拆开，
 * 每块都注入它需要的那几个回调 —— **状态仍然留在 Playground**，
 * 所以拆的时候不用全文件改名（改名才是最容易崩的地方）。
 *
 * ## 为什么必须只对 touch 生效
 *
 * 鼠标上长按会变成「按住不动就弹菜单」，非常烦人。
 * 所以第一件事就是 `ev.pointerType !== 'touch'` 直接返回。
 *
 * ## 三处长按，三种菜单
 *
 *   空白处 → 添加节点菜单
 *   节点上 → 节点菜单
 *   连线上 → 连线菜单
 *
 * 跟右键走的是**同一批处理函数**，所以两边的菜单内容天然一致。
 */
import { onBeforeUnmount, ref } from 'vue'
import { FLOW_DOM } from '@/graph/graphDom'

/** 长按多久算长按 */
const LONG_PRESS_MS = 520
/** 手指移动超过这个距离就当成拖动 / 平移，取消长按 */
const LONG_PRESS_SLOP = 10

/** 长得像图引擎事件的最小结构 —— 不依赖图引擎的类型 */
export interface PointerLikeEvent {
  clientX: number
  clientY: number
  target: EventTarget | null
  preventDefault?: () => void
}

export interface TouchMenuHandlers {
  onNodeMenu: (params: { node: { id: string }; event: PointerLikeEvent }) => void
  /**
   * ⚠️ **收的是事件本身，不是 `{ event }` 包一层**。
   *
   * 它和上面两个不一样：`onNodeMenu` / `onEdgeMenu` 要额外带 node / edge，
   * 所以包了个对象；而「空白画布」没有附加信息，Vue Flow 的
   * `@pane-context-menu` 直接就把事件交出来了。
   *
   * 原来这里写成 `{ event }`，而实现里第一句就是 `ev.preventDefault()` ——
   * 于是长按空白处**必崩**：`ev` 是 `{ event }`，没有 `preventDefault`。
   *
   * ⚠️ 类型检查没拦住它，因为桥接那一层写的是
   * `onPaneMenu: (p) => onPaneMenu(p as never)` —— `as never` 把两边
   * 不兼容的形状抹平了。**这种"为了让类型过关"的断言，正是它藏身的地方。**
   */
  onPaneMenu: (event: PointerLikeEvent) => void
  onEdgeMenu: (params: { edge: { id: string | undefined }; event: PointerLikeEvent }) => void
}

export function useTouchMenu(handlers: TouchMenuHandlers) {
  let timer: number | null = null
  let origin: { x: number; y: number } | null = null
  /**
   * 当前按在屏幕上的手指。
   *
   * ⚠️ 用它判定"第二根手指"。实测反馈：框选模式下双指捏合时，
   * **长按菜单会弹出来** —— 因为原来的逻辑只看第一根手指，
   * 第二根落下时定时器照跑。而"两根手指"是**缩放/平移**，
   * 和"一根手指按住不动"是两种手势，不该混。
   */
  const pointers = new Set<number>()

  /** 是否处于「按下但还没到长按时间」的状态（给宿主做提示用） */
  const pending = ref(false)

  function clear() {
    if (timer !== null) {
      window.clearTimeout(timer)
      timer = null
    }
    origin = null
    pending.value = false
  }

  function onPointerCancelAll() {
    pointers.clear()
    clear()
  }

  function onPointerDown(ev: PointerEvent) {
    if (ev.pointerType !== 'touch') return

    pointers.add(ev.pointerId)

    /*
     * ★ **第二根手指落下 = 这是个双指手势，不是长按。**
     * 立刻取消，并且**不再重新起一个定时器**（所以下面直接 return）。
     */
    if (pointers.size > 1) {
      clear()
      return
    }

    const target = ev.target as HTMLElement | null
    if (!target) return

    // 控件、端口、菜单、面板上的长按有自己的含义，别抢
    /* ⚠️ 选择器从 `graphDom.ts` 取（引擎的 DOM 契约），别在这里写字面量 */
    if (
      target.closest(
        `.nodrag, .${FLOW_DOM.handle}, .np-menu, .np-log, input, textarea, button`,
      )
    ) {
      return
    }

    origin = { x: ev.clientX, y: ev.clientY }
    pending.value = true
    timer = window.setTimeout(() => {
      timer = null
      pending.value = false

      const point = { clientX: origin?.x ?? ev.clientX, clientY: origin?.y ?? ev.clientY }
      const event: PointerLikeEvent = {
        ...point,
        target: ev.target,
      }

      const nodeEl = target.closest(`.${FLOW_DOM.node}`) as HTMLElement | null
      const nodeId = nodeEl?.dataset.id
      if (nodeId) {
        handlers.onNodeMenu({ node: { id: nodeId }, event })
        return
      }

      const edgeEl = target.closest(`.${FLOW_DOM.edge}`) as HTMLElement | null
      if (edgeEl) {
        handlers.onEdgeMenu({ edge: { id: edgeEl.dataset.id }, event })
        return
      }

      handlers.onPaneMenu(event)
    }, LONG_PRESS_MS)
  }

  function onPointerMove(ev: PointerEvent) {
    /* ⚠️ 双指手势期间（第二根手指中途落下）也要保持取消状态 */
    if (pointers.size > 1) {
      clear()
      return
    }
    if (!origin) return
    // 手指动了就是在拖节点 / 平移画布，不是长按
    if (Math.hypot(ev.clientX - origin.x, ev.clientY - origin.y) > LONG_PRESS_SLOP) {
      clear()
    }
  }

  function onPointerUp(ev: PointerEvent) {
    pointers.delete(ev.pointerId)
    clear()
  }

  // 组件销毁时别留下定时器
  onBeforeUnmount(clear)

  return {
    /** 绑到画布容器上：pointerdown / pointerup / pointercancel */
    onPointerDown,
    onPointerCancelAll,
    /** pointermove（通常由宿主的 move 处理里转发进来） */
    onPointerMove,
    onPointerUp,
    pending,
    cancel: clear,
  }
}
